import { useContext, useState } from "preact/hooks";
import { Text } from "preact-i18n";

import { Configs } from "../../helpers/context";
import { getPrimaryButtonStyle, getSwatchColor, isColorAttribute } from "./helpers";
import ProductImage from "./ProductImage";
import ProductPayLinkModifier, { getModifierTotal } from "./ProductPayLinkModifier";
import { formatIntegerCurrencyString } from "../../helpers/formatters";

const ProductPayLinkDetail = ({ product, onBack, onAddToCart }) => {
  const configsContext = useContext(Configs);
  const [skuId, setSkuId] = useState(product.skus[0]?.id);
  const [quantity, setQuantity] = useState(1);
  // modifier id -> array of value ids (LIST) or [text] (TEXT)
  const [modifierValues, setModifierValues] = useState(() =>
    Object.fromEntries(
      (product.modifiers || []).map((m) => [m.id, []]),
    ),
  );

  const sku = product.skus.find((s) => s.id === skuId) || product.skus[0];
  const maxQuantity = sku?.availableQuantity ?? Infinity;
  const outOfStock = !sku || maxQuantity <= 0;
  const modifiers = product.modifiers || [];
  const modifierTotal = getModifierTotal(modifiers, modifierValues);
  const missingRequired = modifiers.some((m) => (modifierValues[m.id] || []).length < m.min);

  const format = (value) =>
    formatIntegerCurrencyString(
      value,
      configsContext?.configs?.currency,
      configsContext?.configs?.ecommerceLocaleLanguage,
    );

  // single attribute (e.g. "Size") -> use it as the variant label
  const attribute = product.attributes.length === 1 ? product.attributes[0] : null;
  const variantLabel = attribute?.label;
  const optionLabel = (s) => (attribute && s.options?.[attribute.name]) || s.displayLabel || s.label || s.code;

  // color variants render as swatches, as long as every value is a recognizable color
  const swatches =
    attribute && isColorAttribute(attribute)
      ? product.skus.map((s) => ({ sku: s, label: optionLabel(s), color: getSwatchColor(optionLabel(s)) }))
      : [];
  const showSwatches = swatches.length > 1 && swatches.every((swatch) => swatch.color);

  function selectSku(id) {
    setSkuId(id);
    setQuantity(1);
  }

  const actionButtonStyle = outOfStock
    ? null
    : getPrimaryButtonStyle(configsContext?.configs?.ecommerceCheckoutButtonColor);

  return (
    <div className="container ppl-detail-section">
      <button type="button" className="ppl-back" onClick={onBack}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M19 12H5M11 6l-6 6 6 6" />
        </svg>
        <span className="ppl-back-text">
          <Text id="ITEM_LAYOUT_BACK">Back</Text>
        </span>
      </button>
      <ProductImage
        className="ppl-detail-picture"
        src={product.imageUrl}
        alt={product.name}
        iconSize={32}
      />
      <div className="ppl-detail-card">
        <h1 className="ppl-detail-name">{product.name}</h1>
        <div className="ppl-detail-price">{format(sku?.price)}</div>
        {product.description ? <p className="ppl-detail-description">{product.description}</p> : null}

        {showSwatches ? (
          <div className="ppl-field">
            <div className="ppl-swatch-heading" id="ppl-swatch-label">
              <span className="ppl-field-label">{variantLabel}</span>
              <span className="ppl-swatch-selected">{sku && optionLabel(sku)}</span>
            </div>
            <div className="ppl-swatches" role="radiogroup" aria-labelledby="ppl-swatch-label">
              {swatches.map(({ sku: s, label, color }) => (
                <button
                  type="button"
                  role="radio"
                  aria-checked={s.id === sku?.id}
                  aria-label={label}
                  title={label}
                  className={`ppl-swatch ${s.id === sku?.id ? "selected" : ""}`.trim()}
                  onClick={() => selectSku(s.id)}
                >
                  <span className="ppl-swatch-color" style={{ backgroundColor: color }} />
                </button>
              ))}
            </div>
          </div>
        ) : product.skus.length > 1 ? (
          <div className="ppl-field">
            <label className="ppl-field-label" htmlFor="ppl-variant">
              {variantLabel || <Text id="PRODUCT_PAYLINK_OPTION">Option</Text>}
            </label>
            <select
              id="ppl-variant"
              className="ppl-select"
              value={skuId}
              onChange={(e) => selectSku(e.target.value)}
            >
              {product.skus.map((s) => (
                <option value={s.id}>{optionLabel(s)}</option>
              ))}
            </select>
          </div>
        ) : null}

        {modifiers.map((modifier) => (
          <ProductPayLinkModifier
            modifier={modifier}
            value={modifierValues[modifier.id] || []}
            format={format}
            onChange={(next) => setModifierValues({ ...modifierValues, [modifier.id]: next })}
          />
        ))}

        <div className="ppl-quantity">
          <span className="ppl-field-label">
            <Text id="PRODUCT_PAYLINK_QUANTITY">Quantity</Text>
          </span>
          <div className="ppl-quantity-controls">
            <button
              type="button"
              aria-label="decrease"
              disabled={quantity <= 1}
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
                <path d="M5 12h14" />
              </svg>
            </button>
            <span>{quantity}</span>
            <button
              type="button"
              aria-label="increase"
              disabled={quantity >= maxQuantity}
              onClick={() => setQuantity(Math.min(maxQuantity, quantity + 1))}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </button>
          </div>
        </div>

        <button
          type="button"
          className="ppl-primary-button"
          style={actionButtonStyle}
          disabled={outOfStock || missingRequired}
          onClick={() => onAddToCart({ product, sku, quantity, modifierValues })}
        >
          {outOfStock ? (
            <Text id="ITEM_LAYOUT_NOT_AVAILABLE">Not Available</Text>
          ) : (
            <span>
              <Text id="PRODUCT_PAYLINK_ADD_TO_CART">Add to cart</Text> · {format((sku.price + modifierTotal) * quantity)}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default ProductPayLinkDetail;
